/**
 * BankAccountConfig Component
 * Interfaccia admin per configurare i conti bancari per bonifici
 */

import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner, Table, Modal, Form, Badge, Tabs, Tab } from 'react-bootstrap';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const BankAccountConfig = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState({});
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    beneficiary: '',
    iban: '',
    bic: '',
    notes: '',
    enabled: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bank-accounts/config/full');
      
      if (response.data.success) {
        setAccounts(response.data.data.bank_accounts);
        setMapping(response.data.data.mapping);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Errore nel caricamento della configurazione');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (account = null) => {
    if (account) {
      setEditingAccount(account.id);
      setFormData({
        id: account.id,
        name: account.name || '',
        beneficiary: account.beneficiary || '',
        iban: account.iban || '',
        bic: account.bic || '',
        notes: account.notes || '',
        enabled: account.enabled || false
      });
    } else {
      setEditingAccount(null);
      setFormData({
        id: '',
        name: '',
        beneficiary: '',
        iban: '',
        bic: '',
        notes: '',
        enabled: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setError(null);
    setSuccess(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (editingAccount) {
        response = await api.put(`/bank-accounts/${editingAccount}`, formData);
      } else {
        response = await api.post('/bank-accounts', formData);
      }

      if (response.data.success) {
        setSuccess(
          editingAccount 
            ? 'Conto bancario aggiornato con successo' 
            : 'Conto bancario creato con successo'
        );
        await fetchConfig();
        setTimeout(() => {
          handleCloseModal();
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving account:', err);
      setError(
        err.response?.data?.message || 
        'Errore durante il salvataggio del conto bancario'
      );
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo conto bancario?')) {
      return;
    }

    try {
      const response = await api.delete(`/bank-accounts/${accountId}`);
      
      if (response.data.success) {
        setSuccess('Conto bancario eliminato con successo');
        await fetchConfig();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(
        err.response?.data?.message || 
        'Errore durante l\'eliminazione del conto bancario'
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleEnabled = async (accountId, currentEnabled) => {
    try {
      const response = await api.put(`/bank-accounts/${accountId}`, {
        enabled: !currentEnabled
      });

      if (response.data.success) {
        setSuccess(`Conto bancario ${!currentEnabled ? 'abilitato' : 'disabilitato'} con successo`);
        await fetchConfig();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error toggling account:', err);
      setError(err.response?.data?.message || 'Errore durante l\'aggiornamento');
      setTimeout(() => setError(null), 5000);
    }
  };

  const validateIBAN = (iban) => {
    const ibanClean = iban.replace(/\s/g, '');
    return /^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/.test(ibanClean);
  };

  if (!['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial'].includes(user?.role)) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Non sei autorizzato ad accedere a questa sezione
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ color: '#006e39' }}>
            <i className="bi bi-bank me-2"></i>
            Configurazione Conti Bancari
          </h2>
          <p className="text-muted">
            Gestisci le coordinate bancarie per i pagamenti con bonifico
          </p>
        </div>
        <Button
          style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
          onClick={() => handleOpenModal()}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Nuovo Conto
        </Button>
      </div>

      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" style={{ color: '#006e39' }} />
          <p className="mt-3">Caricamento configurazione...</p>
        </div>
      ) : (
        <Card className="shadow-sm">
          <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Conti Bancari Configurati
            </h5>
          </Card.Header>
          <Card.Body>
            {Object.keys(accounts).length === 0 ? (
              <Alert variant="info">
                <i className="bi bi-info-circle me-2"></i>
                Nessun conto bancario configurato. Clicca su "Nuovo Conto" per iniziare.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Beneficiario</th>
                      <th>IBAN</th>
                      <th>Stato</th>
                      <th style={{ width: '180px' }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(accounts).map(account => (
                      <tr key={account.id}>
                        <td>
                          <strong>{account.name}</strong>
                          <br />
                          <small className="text-muted">{account.id}</small>
                        </td>
                        <td>{account.beneficiary || <span className="text-muted">-</span>}</td>
                        <td>
                          {account.iban ? (
                            <span style={{ fontFamily: 'monospace' }}>{account.iban}</span>
                          ) : (
                            <span className="text-muted">Non configurato</span>
                          )}
                        </td>
                        <td>
                          {account.enabled ? (
                            <Badge bg="success">
                              <i className="bi bi-check-circle me-1"></i>
                              Abilitato
                            </Badge>
                          ) : (
                            <Badge bg="secondary">
                              <i className="bi bi-x-circle me-1"></i>
                              Disabilitato
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleOpenModal(account)}
                              title="Modifica"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              size="sm"
                              variant={account.enabled ? 'outline-warning' : 'outline-success'}
                              onClick={() => handleToggleEnabled(account.id, account.enabled)}
                              title={account.enabled ? 'Disabilita' : 'Abilita'}
                            >
                              <i className={`bi bi-${account.enabled ? 'pause' : 'play'}-circle`}></i>
                            </Button>
                            {account.id !== mapping.default && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(account.id)}
                                title="Elimina"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal Crea/Modifica */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAccount ? 'Modifica Conto Bancario' : 'Nuovo Conto Bancario'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>
                ID Conto <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="id"
                value={formData.id}
                onChange={handleChange}
                placeholder="es. uisp_regional_puglia"
                disabled={!!editingAccount}
                required
              />
              <Form.Text className="text-muted">
                Identificativo univoco (snake_case, non modificabile dopo creazione)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Nome <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="es. UISP Puglia"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Beneficiario <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="beneficiary"
                value={formData.beneficiary}
                onChange={handleChange}
                placeholder="Nome completo del beneficiario"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                IBAN <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="IT00X0000000000000000000000"
                isInvalid={formData.iban && !validateIBAN(formData.iban)}
                required
              />
              <Form.Control.Feedback type="invalid">
                Formato IBAN non valido (deve essere un IBAN italiano)
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>BIC/SWIFT (opzionale)</Form.Label>
              <Form.Control
                type="text"
                name="bic"
                value={formData.bic}
                onChange={handleChange}
                placeholder="es. UNCRITMM"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Note (opzionale)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Note o istruzioni aggiuntive per le associazioni..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleChange}
                label="Abilita questo conto bancario"
              />
              <Form.Text className="text-muted">
                Solo i conti abilitati verranno usati nelle email di pagamento
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
              disabled={formData.iban && !validateIBAN(formData.iban)}
            >
              {editingAccount ? 'Salva Modifiche' : 'Crea Conto'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default BankAccountConfig;
